<template>
  <b-card
    title="Контрольные точки"
    class="mt-3 checkpoints-card w-100"
    :class="layoutClass"
  >
    <b-alert variant="info" show class="hints-alert">
      <div class="d-flex align-items-start">
        <div class="hint-icon">💡</div>
        <div class="hint-content">
          <div class="hint-title">Как устроена таблица</div>
          <div class="hint-description">
            Порядок строк задаёт ожидаемую последовательность контрольных точек для анализатора.
            Наведите курсор на заголовок колонки или на значок «?» — там кратко, как поле влияет на расчёт.
          </div>
          <div class="hint-list">
            <div class="hint-list-item">
              <strong>Точка</strong> — имя точки в потоке считываний (как в данных). Совпадает с идентификатором ридера/точки; один и тот же код на двух строках допустим (например, один ридер на выезде и на заезде).
            </div>
            <div class="hint-list-item">
              <strong>Резерв</strong> — дополнительный идентификатор антенны; считывания с него считаются прохождением той же строки, что и «Точка».
            </div>
            <div class="hint-list-item">
              <strong>Круг</strong> — номер круга для многоэтапных трасс; разделяет повторные проходы одной логической точки.
            </div>
            <div class="hint-list-item">
              <strong>Время (сек)</strong> — ожидаемое время <strong>от старта гонки (ган)</strong> до <em>этой</em> контрольной точки, в секундах; в каждой строке — полное время от момента старта до данной точки, значения по строкам не суммируются. Анализатор использует его как ожидаемый момент прохождения при привязке считываний вместе с допуском tolerance, если он задан для точки.
            </div>
            <div class="hint-list-item">
              <strong>Время от пред.</strong> — минимум и максимум интервала в секундах между временем на предыдущей точке и на этой (ввод мм:сс). При выходе за пределы чекпоинт помечается нарушением временных ограничений.
            </div>
            <div class="hint-list-item">
              <strong>Заголовок</strong> — подпись в протоколе и отчётах; на сопоставление считываний не влияет.
            </div>
            <div class="hint-list-item">
              <strong>Приоритет</strong> — если для точки остаётся несколько подходящих вариантов времени: «Мин» — самое раннее, «Макс» — самое позднее, «Средн.» — среднее. «По умолчанию» — общее правило из параметров анализа. При равенстве времён учитываются предпочтительная антенна и RSSI.
            </div>
          </div>
        </div>
      </div>
    </b-alert>

    <b-alert v-if="localCheckpoints.length > 0 && !trackValidForMode" variant="warning" show class="mb-3">
      <strong>Внимание:</strong>
      <span v-if="isChipTimeMode">В режиме Chip Time нужно минимум <strong>две</strong> контрольные точки (первая и последняя в списке — границы сегмента).</span>
      <span v-else>Добавьте checkpoint с точкой FINISH. Конфигурация не сохранится без финишной точки.</span>
    </b-alert>

    <b-alert v-if="isChipTimeMode && localCheckpoints.length >= 2" variant="success" show class="mb-3">
      <strong>Chip Time:</strong> первая точка в списке — начало сегмента, последняя — финиш (бейджи Chip start/finish). Резервные считыватели можно настроить на <strong>любой</strong> контрольной точке.
    </b-alert>

    <!-- Checkpoints list -->
    <div v-if="localCheckpoints.length > 0" class="checkpoints-list w-100">
      <!-- Table header: columns sum to 12 for full-width row -->
      <b-row class="checkpoints-header checkpoints-header-row mb-2 mx-0 align-items-start">
        <b-col cols="12" md="2" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('point')"
          >
            Точка&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="6" sm="3" md="1" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('backup')"
          >
            Резерв&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="6" sm="3" md="1" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('lap')"
          >
            Круг&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="6" sm="3" md="1" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('expectedSegmentSec')"
          >
            Время (сек)&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="12" md="3" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('timeFromPrevious')"
          >
            Время от пред.&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="12" sm="6" md="2" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('header')"
          >
            Заголовок&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col cols="6" sm="3" md="1" class="cp-head-cell">
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('priority')"
          >
            Приоритет&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
        <b-col
          cols="6"
          sm="3"
          md="1"
          class="cp-head-cell cp-head-cell--actions"
        >
          <small
            class="text-muted cp-col-head"
            tabindex="0"
            v-b-tooltip.hover.top="cpColTooltip('actions')"
          >
            Действия&nbsp;<span class="cp-col-hint" aria-hidden="true">?</span>
          </small>
        </b-col>
      </b-row>

      <template v-for="(cp, index) in localCheckpoints">
        <b-form-row
          :key="'cp-' + index"
          class="align-items-end mb-1 checkpoint-row mx-0"
          :class="chipRowClass(index)"
        >
        <b-col cols="12" md="2" class="checkpoint-col-point">
          <div
            class="checkpoint-chip-badges"
            :class="{ 'checkpoint-chip-badges--empty': !chipBoundaryBadges(index).length }"
          >
            <template v-if="chipBoundaryBadges(index).length">
              <b-badge
                v-for="b in chipBoundaryBadges(index)"
                :key="b.text"
                :variant="b.variant"
                class="mr-1 mb-1"
              >{{ b.text }}</b-badge>
            </template>
          </div>
          <b-form-input
            v-model="cp.point"
            placeholder="START, CP1, FINISH"
            size="sm"
            class="checkpoint-input-grow"
            @input="emitUpdate"
          />
        </b-col>
        <b-col cols="6" sm="3" md="1">
          <b-form-input
            v-model="cp.backupAntennaId"
            placeholder="—"
            size="sm"
            class="checkpoint-input-grow"
            @input="emitUpdate"
          />
        </b-col>
        <b-col cols="6" sm="3" md="1">
          <b-form-input
            v-model.number="cp.lap"
            type="number"
            placeholder="1"
            size="sm"
            class="checkpoint-input-grow"
            @input="emitUpdate"
          />
        </b-col>
        <b-col cols="6" sm="3" md="1">
          <b-form-input
            v-model.number="cp.expectedTime"
            type="number"
            placeholder="300"
            size="sm"
            class="checkpoint-input-grow"
            @input="emitUpdate"
          />
        </b-col>
        <b-col cols="12" md="3">
          <div class="time-range-input">
            <b-form-input
              :value="formatSecondsToMmSs(cp.minTimeFromPrevious)"
              type="text"
              placeholder="Мин mm:ss"
              size="sm"
              @input="e => updateTimeConstraint(cp, 'minTimeFromPrevious', e)"
            />
            <span class="range-separator">—</span>
            <b-form-input
              :value="formatSecondsToMmSs(cp.maxTimeFromPrevious)"
              type="text"
              placeholder="Макс mm:ss"
              size="sm"
              @input="e => updateTimeConstraint(cp, 'maxTimeFromPrevious', e)"
            />
          </div>
        </b-col>
        <b-col cols="12" sm="6" md="2">
          <b-form-input
            v-model="cp.header"
            placeholder="Старт"
            size="sm"
            class="checkpoint-input-grow"
            @input="emitUpdate"
          />
        </b-col>
        <b-col cols="6" sm="3" md="1">
          <b-form-select
            v-model="cp.priorityRule"
            :options="priorityRuleOptions"
            size="sm"
            class="checkpoint-input-grow"
            @change="emitUpdate"
          />
        </b-col>
        <b-col
          cols="6"
          sm="3"
          md="1"
          class="checkpoint-col-actions"
        >
          <div
            class="checkpoint-actions"
            role="group"
            aria-label="Порядок и удаление строки"
          >
            <div
              class="btn-group checkpoint-actions__reorder"
              role="group"
            >
              <b-button
                v-b-tooltip.hover.top="'Переместить точку выше в списке (меняется ожидаемый порядок прохождения в анализе)'"
                variant="outline-secondary"
                size="sm"
                class="checkpoint-action-btn"
                :disabled="index === 0"
                @click="moveUp(index)"
              >
                ↑
              </b-button>
              <b-button
                v-b-tooltip.hover.top="'Переместить точку ниже в списке'"
                variant="outline-secondary"
                size="sm"
                class="checkpoint-action-btn"
                :disabled="index === localCheckpoints.length - 1"
                @click="moveDown(index)"
              >
                ↓
              </b-button>
            </div>
            <b-button
              v-b-tooltip.hover.top="'Удалить эту контрольную точку из конфигурации трассы'"
              variant="danger"
              size="sm"
              class="checkpoint-action-btn checkpoint-action-btn--delete"
              @click="removeCheckpoint(index)"
            >
              ✕
            </b-button>
          </div>
        </b-col>
      </b-form-row>
      </template>
    </div>

    <div v-else class="text-center text-muted py-3">
      Нет добавленных контрольных точек
    </div>

    <!-- Add checkpoint buttons -->
    <div class="mt-3">
      <b-button 
        v-b-tooltip.hover.top="'Добавить новую контрольную точку вручную'"
        size="sm" 
        variant="primary" 
        @click="addCheckpoint"
      >
        + Добавить точку
      </b-button>
      <b-button 
        v-b-tooltip.hover.top="'Быстро добавить стандартные точки START и FINISH'"
        size="sm" 
        variant="outline-primary" 
        class="ml-2" 
        @click="addStandardPoints"
      >
        + Добавить стандартные (START, FINISH)
      </b-button>
    </div>

    <!-- Checkpoint statistics -->
    <div v-if="localCheckpoints.length > 0" class="mt-3 p-2 bg-light rounded">
      <small class="text-muted">
        Всего точек: {{ localCheckpoints.length }}
        | Кругов: {{ maxLaps }}
      </small>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BFormRow,
  BCol,
  BFormInput,
  BFormSelect,
  BButton,
  BAlert,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'

export default {
  name: 'CheckpointsList',
  components: {
    BCard,
    BRow,
    BFormRow,
    BCol,
    BFormInput,
    BFormSelect,
    BButton,
    BAlert,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    checkpoints: {
      type: Array,
      default: () => [],
    },
    officialTimeMode: {
      type: String,
      default: 'gunTime',
    },
  },
  data() {
    return {
      localCheckpoints: [],
      layoutClass: 'light-layout',
      themeObserver: null,
      priorityRuleOptions: [
        { value: undefined, text: 'По умолчанию' },
        { value: 'min', text: 'Мин' },
        { value: 'max', text: 'Макс' },
        { value: 'average', text: 'Средн.' },
      ],
      /** Tooltip strings for column headers (aligned with race-analysis-core). */
      fieldHints: {
        point:
          'Идентификатор точки в потоке считываний (как в данных с ридера). Порядок строк задаёт ожидаемую последовательность прохождения. Одно имя на нескольких строках допустимо, если один физический ридер проходится несколько раз.',
        backup:
          'Дополнительный идентификатор антенны/резерва: считывания с него обрабатываются как эта же контрольная точка, что и колонка «Точка».',
        lap:
          'Номер круга на многоэтапной трассе; помогает отличать повторные проходы одной логической точки.',
        expectedSegmentSec:
          'Ожидаемое время от старта гонки (ган) до этой контрольной точки, в секундах. В каждой строке задаётся полное время от момента старта до ожидаемого прохождения данной точки; значения по строкам не суммируются (например, 100 с и 300 с на разных контрольных точках — оба отсчёта от старта). Используется как ожидаемый момент прохождения при привязке считываний вместе с допуском tolerance точки, если задан.',
        timeFromPrevious:
          'Минимум и максимум интервала в секундах между временем на предыдущей контрольной точке и на этой (ввод мм:сс). Если фактический интервал выходит за пределы, чекпоинт помечается нарушением временных ограничений.',
        header:
          'Подпись в протоколе и отчётах. На сопоставление считываний с точками не влияет.',
        priority:
          'Если для точки остаётся несколько подходящих вариантов времени: «Мин» — самое раннее, «Макс» — самое позднее, «Средн.» — среднее арифметическое. «По умолчанию» — правило из общих параметров анализа. При равенстве времён учитываются предпочтительная антенна и RSSI.',
        actions:
          'Перестановка строк меняет логический порядок точек в анализе; удаление убирает точку из трассы.',
      },
    }
  },
  computed: {
    maxLaps() {
      if (this.localCheckpoints.length === 0) return 0
      return Math.max(...this.localCheckpoints.map(cp => cp.lap || 1))
    },
    hasFinishCheckpoint() {
      return this.localCheckpoints.some(cp =>
        cp.point === 'FINISH' || (cp.point && cp.point.toUpperCase().includes('FINISH'))
      )
    },
    isChipTimeMode() {
      return this.officialTimeMode === 'chipTime'
    },
    trackValidForMode() {
      if (this.isChipTimeMode) {
        return this.localCheckpoints.length >= 2
      }
      return this.hasFinishCheckpoint
    },
  },
  watch: {
    checkpoints: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.localCheckpoints = JSON.parse(JSON.stringify(newVal || []))
      },
    },
  },
  mounted() {
    this.setupThemeObserver()
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
  },
  methods: {
    /** Bootstrap-Vue tooltip config: wide readable box for column hints. */
    cpColTooltip(key) {
      const title = this.fieldHints[key]
      if (!title) return { title: '' }
      return {
        title,
        customClass: 'cp-checkpoint-field-tooltip',
      }
    },
    // A6: mm:ss format for time constraints
    formatSecondsToMmSs(sec) {
      if (sec == null || sec === '' || !Number.isFinite(Number(sec))) return ''
      const s = Math.floor(Number(sec))
      const m = Math.floor(s / 60)
      const remainderSec = s % 60
      return `${m}:${String(remainderSec).padStart(2, '0')}`
    },
    parseMmSsToSeconds(str) {
      if (!str || typeof str !== 'string') return undefined
      const trimmed = str.trim()
      if (!trimmed) return undefined
      const parts = trimmed.split(':')
      if (parts.length === 2) {
        const m = parseInt(parts[0], 10)
        const s = parseInt(parts[1], 10)
        if (Number.isFinite(m) && Number.isFinite(s) && s >= 0 && s < 60) {
          return m * 60 + s
        }
      }
      return undefined
    },
    updateTimeConstraint(cp, field, event) {
      const value = event && event.target ? event.target.value : event
      const seconds = this.parseMmSsToSeconds(value)
      if (seconds !== undefined) {
        this.$set(cp, field, seconds)
      } else if (value === '' || value == null) {
        this.$set(cp, field, undefined)
      }
      this.emitUpdate()
    },
    detectTheme() {
      const html = document.documentElement
      const body = document.body
      
      if (html.classList.contains('dark-layout') || 
          body.classList.contains('dark-layout') ||
          html.classList.contains('theme-dark')) {
        this.layoutClass = 'dark-layout'
      } else {
        this.layoutClass = 'light-layout'
      }
    },

    setupThemeObserver() {
      this.detectTheme()
      
      const observerConfig = { attributes: true, attributeFilter: ['class'] }
      this.themeObserver = new MutationObserver(() => {
        this.detectTheme()
      })
      
      this.themeObserver.observe(document.documentElement, observerConfig)
      this.themeObserver.observe(document.body, observerConfig)
    },

    addCheckpoint() {
      this.localCheckpoints.push({
        point: '',
        lap: 1,
        expectedTime: null,
        header: '',
      })
      this.emitUpdate()
    },

    addStandardPoints() {
      const hasStart = this.localCheckpoints.some(cp => cp.point === 'START')
      const hasFinish = this.localCheckpoints.some(cp => cp.point === 'FINISH')

      if (!hasStart) {
        this.localCheckpoints.unshift({
          point: 'START',
          lap: 0,
          expectedTime: 0,
          header: 'Старт',
        })
      }

      if (!hasFinish) {
        this.localCheckpoints.push({
          point: 'FINISH',
          lap: 1,
          expectedTime: null,
          header: 'Финиш',
        })
      }

      this.emitUpdate()
      
      this.$bvToast.toast('Стандартные точки добавлены', {
        title: 'Успешно',
        variant: 'success',
        solid: true,
      })
    },

    removeCheckpoint(index) {
      this.localCheckpoints.splice(index, 1)
      this.emitUpdate()
    },

    moveUp(index) {
      if (index === 0) return
      const temp = this.localCheckpoints[index]
      this.$set(this.localCheckpoints, index, this.localCheckpoints[index - 1])
      this.$set(this.localCheckpoints, index - 1, temp)
      this.emitUpdate()
    },

    moveDown(index) {
      if (index === this.localCheckpoints.length - 1) return
      const temp = this.localCheckpoints[index]
      this.$set(this.localCheckpoints, index, this.localCheckpoints[index + 1])
      this.$set(this.localCheckpoints, index + 1, temp)
      this.emitUpdate()
    },

    emitUpdate() {
      this.$emit('update', this.localCheckpoints)
    },
    chipBoundaryBadges(index) {
      if (!this.isChipTimeMode || this.localCheckpoints.length < 2) return []
      const out = []
      if (index === 0) out.push({ text: 'Chip start', variant: 'success' })
      if (index === this.localCheckpoints.length - 1) out.push({ text: 'Chip finish', variant: 'primary' })
      return out
    },
    chipRowClass(index) {
      if (!this.isChipTimeMode || this.localCheckpoints.length < 2) return []
      if (index === 0) return ['checkpoint-row--chip-start']
      if (index === this.localCheckpoints.length - 1) return ['checkpoint-row--chip-finish']
      return []
    },
  },
}
</script>

<style scoped>
/* Full-width card body (Bootstrap-Vue card often shrinks content) */
.checkpoints-card.w-100 {
  max-width: 100%;
}

.checkpoints-card >>> .card-body {
  width: 100%;
  max-width: 100%;
}

/* Common structure styles */
.checkpoints-list {
  width: 100%;
  max-width: 100%;
  max-height: 500px;
  overflow-y: auto;
  padding: 0.571rem 0;
  border-radius: 0.428rem;
  transition: background 0.3s ease;
}

.checkpoint-col-point {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.checkpoint-chip-badges {
  min-height: 0;
  line-height: 1;
}

.checkpoint-chip-badges:not(.checkpoint-chip-badges--empty) {
  margin-bottom: 0.25rem;
}

.checkpoint-input-grow {
  width: 100%;
  min-width: 0;
}

.checkpoint-col-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-left: 0.35rem;
}

.checkpoint-actions {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: flex-end;
  gap: 0.35rem;
  width: 100%;
  max-width: 100%;
}

.checkpoint-actions__reorder {
  flex-shrink: 0;
}

/* Same box height for outline + danger; vertical center for glyphs */
.checkpoint-action-btn {
  min-width: 2rem;
  min-height: 2rem;
  padding: 0 0.35rem !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.checkpoint-action-btn--delete {
  flex-shrink: 0;
}

/* Header row: top-left labels; same horizontal rhythm as body (actions col) */
.checkpoints-header-row {
  row-gap: 0.35rem;
}

.cp-head-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  min-width: 0;
}

.cp-head-cell .cp-col-head {
  width: 100%;
  text-align: left;
}

.cp-head-cell--actions {
  padding-left: 0.35rem;
}

@media (max-width: 767.98px) {
  .checkpoint-col-actions {
    justify-content: flex-start;
  }
}

.checkpoints-header {
  padding: 0.857rem 1rem;
  border-radius: 0.428rem;
  margin-bottom: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem;
  transition: all 0.3s ease;
}

.cp-col-head {
  cursor: help;
  display: block;
  text-align: left;
  max-width: 100%;
}

.cp-col-hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: 0.1em;
  min-width: 14px;
  height: 14px;
  padding: 0 2px;
  border-radius: 50%;
  border: 1px solid currentColor;
  font-size: 9px;
  line-height: 1;
  opacity: 0.75;
  flex-shrink: 0;
  white-space: nowrap;
}

.checkpoint-row {
  padding: 0.857rem;
  border-radius: 0.428rem;
  transition: all 0.3s ease;
  margin-bottom: 0.857rem;
}

.checkpoints-card.light-layout .checkpoint-row--chip-start {
  border-left: 4px solid #28c76f;
}

.checkpoints-card.light-layout .checkpoint-row--chip-finish {
  border-left: 4px solid #7367f0;
}

.checkpoints-card.dark-layout .checkpoint-row--chip-start {
  border-left: 4px solid #39da8a;
}

.checkpoints-card.dark-layout .checkpoint-row--chip-finish {
  border-left: 4px solid #9d93f6;
}

/* Light theme - checkpoints list */
.checkpoints-card.light-layout .checkpoints-list {
  background: #fafafa;
}

.checkpoints-card.light-layout .checkpoints-header {
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
}

.checkpoints-card.light-layout .checkpoints-header .text-muted {
  color: #6e6b7b !important;
}

.checkpoints-card.light-layout .checkpoint-row {
  background: #fff;
  border: 1px solid #ebe9f1;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.checkpoints-card.light-layout .checkpoint-row:hover {
  border-color: #7367f0;
  box-shadow: 0 4px 12px 0 rgba(115, 103, 240, 0.15);
  transform: translateY(-1px);
}

/* Dark theme - checkpoints list */
.checkpoints-card.dark-layout .checkpoints-list {
  background: rgba(26, 30, 52, 0.3);
}

.checkpoints-card.dark-layout .checkpoints-header {
  background: rgba(37, 43, 66, 0.5);
  border: 1px solid #3b4253;
}

.checkpoints-card.dark-layout .checkpoints-header .text-muted {
  color: #a8aaae !important;
}

.checkpoints-card.dark-layout .checkpoint-row {
  background: linear-gradient(135deg, rgba(37, 43, 66, 0.95), rgba(30, 36, 57, 0.95));
  border: 1px solid rgba(59, 66, 83, 0.8);
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.3);
}

.checkpoints-card.dark-layout .checkpoint-row:hover {
  border-color: #9d93f6;
  box-shadow: 0 6px 20px 0 rgba(157, 147, 246, 0.25);
  transform: translateY(-2px);
}

.gap-1 {
  gap: 0.286rem;
}

/* Action buttons styling */
.checkpoints-card .btn-outline-secondary {
  transition: all 0.2s ease;
}

.checkpoints-card.dark-layout .btn-outline-secondary {
  background: rgba(59, 66, 83, 0.3);
  border-color: #3b4253;
  color: #d0d2d6;
}

.checkpoints-card.dark-layout .btn-outline-secondary:hover:not(:disabled) {
  background: rgba(157, 147, 246, 0.2);
  border-color: #9d93f6;
  color: #fff;
  transform: scale(1.05);
}

.checkpoints-card.dark-layout .btn-outline-secondary:disabled {
  background: rgba(59, 66, 83, 0.1);
  border-color: rgba(59, 66, 83, 0.3);
  color: rgba(168, 170, 174, 0.4);
}

.checkpoints-card.dark-layout .btn-danger {
  background: linear-gradient(135deg, #ea5455, #d63031);
  border-color: transparent;
  box-shadow: 0 2px 6px 0 rgba(234, 84, 85, 0.3);
}

.checkpoints-card.dark-layout .btn-danger:hover {
  background: linear-gradient(135deg, #f76c6d, #ea5455);
  box-shadow: 0 4px 12px 0 rgba(234, 84, 85, 0.5);
  transform: scale(1.05);
}

.checkpoints-card.light-layout .btn-outline-secondary:hover:not(:disabled) {
  transform: scale(1.05);
}

.checkpoints-card.light-layout .btn-danger:hover {
  transform: scale(1.05);
}

/* Add buttons at bottom */
.checkpoints-card.dark-layout .btn-primary {
  background: linear-gradient(135deg, #9d93f6, #7367f0);
  border-color: transparent;
  box-shadow: 0 2px 6px 0 rgba(115, 103, 240, 0.3);
}

.checkpoints-card.dark-layout .btn-primary:hover {
  background: linear-gradient(135deg, #b4a9ff, #9d93f6);
  box-shadow: 0 4px 12px 0 rgba(157, 147, 246, 0.5);
  transform: translateY(-1px);
}

.checkpoints-card.dark-layout .btn-success {
  background: linear-gradient(135deg, #39da8a, #28c76f);
  border-color: transparent;
  box-shadow: 0 2px 6px 0 rgba(40, 199, 111, 0.3);
}

.checkpoints-card.dark-layout .btn-success:hover {
  background: linear-gradient(135deg, #5ede9c, #39da8a);
  box-shadow: 0 4px 12px 0 rgba(57, 218, 138, 0.5);
  transform: translateY(-1px);
}

.time-range-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.time-range-input .form-control {
  flex: 1;
}

.range-separator {
  color: #b9b9c3;
  font-weight: 500;
  user-select: none;
  flex-shrink: 0;
}

/* Light theme - hints */
.checkpoints-card.light-layout .hint-title {
  color: #5e5873;
}

.checkpoints-card.light-layout .hint-description,
.checkpoints-card.light-layout .hint-list-item {
  color: #6e6b7b;
}

.checkpoints-card.light-layout .hint-list-item strong {
  color: #5e5873;
}

/* Dark theme - hints */
.checkpoints-card.dark-layout .hint-title {
  color: #fff;
}

.checkpoints-card.dark-layout .hint-description,
.checkpoints-card.dark-layout .hint-list-item {
  color: #d0d2d6;
}

.checkpoints-card.dark-layout .hint-list-item strong {
  color: #fff;
}

.checkpoints-card.dark-layout .range-separator {
  color: #a8aaae;
}

/* Hints alert styling */
.hints-alert {
  padding: 1.143rem !important;
}

.hint-icon {
  font-size: 1.5rem;
  line-height: 1;
  margin-right: 1rem;
  flex-shrink: 0;
}

.hint-content {
  flex: 1;
}

.hint-title {
  font-weight: 600;
  margin-bottom: 0.571rem;
  font-size: 0.95rem;
}

.hint-description {
  margin-bottom: 0.857rem;
  line-height: 1.5;
}

.hint-list {
  display: flex;
  flex-direction: column;
  gap: 0.428rem;
}

.hint-list-item {
  padding-left: 1rem;
  position: relative;
  font-size: 0.857rem;
  line-height: 1.5;
}

.hint-list-item::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #00cfe8;
  font-weight: bold;
}

.hint-list-item strong {
  font-weight: 600;
}
</style>

<style>
.tooltip.cp-checkpoint-field-tooltip .tooltip-inner {
  max-width: 22rem;
  text-align: left;
  font-size: 0.8125rem;
  line-height: 1.45;
}
</style>

